import type { OperationResponse } from '../contracts/common'
import { ApiError, isApiError } from '../errors/ApiError'
import { mapApiError } from '../errors/mapApiError'
import { buildApiUrl, type QueryParameters } from './queryString'

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

export interface ApiRequestOptions<TBody = unknown> {
  method?: HttpMethod
  query?: QueryParameters
  body?: TBody
  headers?: HeadersInit
  signal?: AbortSignal
  timeoutMs?: number
  locale?: 'ar' | 'en'
}

export interface ApiClientOptions {
  baseUrl: string
  timeoutMs: number
  getAccessToken?: () => string | null | Promise<string | null>
  fetchImplementation?: typeof fetch
}

function createCombinedSignal(external: AbortSignal | undefined, timeoutMs: number) {
  const controller = new AbortController()
  const timeoutController = new AbortController()
  const onExternalAbort = () => controller.abort(external?.reason)
  const onTimeout = () => {
    timeoutController.abort()
    controller.abort(timeoutController.signal.reason)
  }
  if (external?.aborted) onExternalAbort()
  else external?.addEventListener('abort', onExternalAbort, { once: true })
  const timeoutId = globalThis.setTimeout(onTimeout, timeoutMs)
  return {
    signal: controller.signal,
    didTimeout: () => timeoutController.signal.aborted,
    cleanup: () => {
      globalThis.clearTimeout(timeoutId)
      external?.removeEventListener('abort', onExternalAbort)
    },
  }
}

async function parseResponse(response: Response) {
  if (response.status === 204) return undefined
  const text = await response.text()
  if (!text) return undefined
  try {
    return JSON.parse(text) as unknown
  } catch (cause) {
    throw new ApiError('parsing', 'The server returned an unreadable response.', response.status, {}, cause)
  }
}

export class ApiClient {
  private readonly fetchImplementation: typeof fetch

  constructor(private readonly options: ApiClientOptions) {
    this.fetchImplementation = options.fetchImplementation ?? globalThis.fetch.bind(globalThis)
  }

  async request<TResponse, TBody = unknown>(path: string, options: ApiRequestOptions<TBody> = {}): Promise<TResponse> {
    const combined = createCombinedSignal(options.signal, options.timeoutMs ?? this.options.timeoutMs)
    try {
      const token = await this.options.getAccessToken?.()
      const headers = new Headers(options.headers)
      headers.set('Accept', 'application/json')
      if (options.locale) {
        headers.set('Accept-Language', options.locale)
        headers.set('lang', options.locale)
      }
      if (token) headers.set('Authorization', `Bearer ${token}`)
      const hasBody = options.body !== undefined
      const isFormData = hasBody && typeof FormData !== 'undefined' && options.body instanceof FormData
      if (isFormData) headers.delete('Content-Type')
      else if (hasBody && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')

      const response = await this.fetchImplementation(buildApiUrl(this.options.baseUrl, path, options.query), {
        method: options.method ?? 'GET',
        headers,
        body: hasBody ? (isFormData ? options.body as FormData : JSON.stringify(options.body)) : undefined,
        signal: combined.signal,
      })
      const payload = await parseResponse(response)
      if (!response.ok) throw mapApiError(response.status, payload)
      return payload as TResponse
    } catch (error) {
      if (isApiError(error)) throw error
      if (combined.signal.aborted) {
        throw combined.didTimeout()
          ? new ApiError('timeout', 'The request timed out.', undefined, {}, error)
          : new ApiError('cancelled', 'The request was cancelled.', undefined, {}, error)
      }
      if (error instanceof TypeError) throw new ApiError('network', 'The service is currently unreachable.', undefined, {}, error)
      throw new ApiError('unknown', 'The request could not be completed.', undefined, {}, error)
    } finally {
      combined.cleanup()
    }
  }

  get<TResponse>(path: string, options?: Omit<ApiRequestOptions, 'method' | 'body'>) {
    return this.request<TResponse>(path, options)
  }

  post<TResponse, TBody>(path: string, body: TBody, options?: Omit<ApiRequestOptions<TBody>, 'method' | 'body'>) {
    return this.request<TResponse, TBody>(path, { ...options, method: 'POST', body })
  }
}

export function unwrapApiResponse<T>(value: T | OperationResponse<T>): T {
  if (value && typeof value === 'object') {
    const envelope = value as OperationResponse<T>
    if (envelope.data !== undefined) return envelope.data
    if (envelope.Data !== undefined) return envelope.Data
  }
  return value as T
}
